import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchLibrariesByChitalishteId,
  fetchLibraryInformationCards,
  selectLibraries,
  selectLibraryInformationCards,
  selectLibraryLoading
} from '../../store/slices/librarySlice';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar
} from '@mui/material';
import {
  LibraryBooks,
  People,
  Book,
  Computer,
  Event,
  ExpandMore as ExpandMoreIcon,
  TrendingUp,
  AccessTime,
  School,
  Print,
  BarChart,
  Numbers,
  Group,
  MenuBook,
  EventNote,
  CompareArrows,
  Home,
  Work,
  Language,
  Public,
  Description,
  Phone,
  Email,
  LocationOn,
  Person,
  Accessible,
  Project,
  TrendingFlat,
  Visibility,
  LocalLibrary,
  EmojiEvents,
  BusinessCenter
} from '@mui/icons-material';

const LibraryCard = ({ library, informationCards, isFirst }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Сортираме картите по година (най-новата първа)
  const sortedCards = informationCards && informationCards.length > 0 
    ? [...informationCards].sort((a, b) => b.year - a.year)
    : [];
  
  const latestCard = sortedCards.length > 0 ? sortedCards[0] : null;
  const previousCard = sortedCards.length > 1 ? sortedCards[1] : null;

  // Изчисляваме промените спрямо предходната година
  const calculateChange = (current, previous, field) => {
    if (!current || !previous || current[field] === null || previous[field] === null) {
      return null;
    }
    const currentVal = current[field] || 0;
    const previousVal = previous[field] || 0;
    if (previousVal === 0) return currentVal > 0 ? 100 : 0;
    return ((currentVal - previousVal) / previousVal * 100).toFixed(1);
  };

  return (
    <Card sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Заглавие на библиотеката */}
        <Box sx={{ 
          p: 3, 
          bgcolor: isFirst ? 'primary.main' : 'secondary.main',
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LibraryBooks sx={{ mr: 2, fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {library.name}
              </Typography>
              {/* <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {library.town && (
                  <Chip 
                    icon={<LocationOn />} 
                    label={library.town} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                )}
                {library.registrationNumber && (
                  <Chip 
                    icon={<Numbers />} 
                    label={`Рег. №: ${library.registrationNumber}`} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                )}
              </Box> */}
            </Box>
          </Box>
        </Box>

        {/* Основна информация за библиотеката */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Контактна информация */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person fontSize="small" /> Контактна информация
                </Typography>
                <Grid container spacing={1}>
                  {library.address && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Home fontSize="small" color="action" />
                        <Typography variant="body2">{library.address}</Typography>
                      </Box>
                    </Grid>
                  )}
                  {library.phone && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{library.phone}</Typography>
                      </Box>
                    </Grid>
                  )}
                  {library.email && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{library.email}</Typography>
                      </Box>
                    </Grid>
                  )}
                  {library.chairman && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2"><strong>Председател:</strong> {library.chairman}</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Бързи статистики - само ако имаме данни */}
            {latestCard && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart fontSize="small" /> Ключови показатели за {latestCard.year} г.
                  </Typography>
                </Grid>
                
                {/* Членове и посещения */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group fontSize="small" /> Читатели и посещения
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <People />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Общо читатели</Typography>
                          <Typography variant="h5">{latestCard.totalMembersCount || 0}</Typography>
                          {/* {previousCard && calculateChange(latestCard, previousCard, 'totalMembersCount') && (
                            <Chip 
                              label={`${calculateChange(latestCard, previousCard, 'totalMembersCount')}%`}
                              size="small"
                              color={calculateChange(latestCard, previousCard, 'totalMembersCount') > 0 ? "success" : "error"}
                              sx={{ mt: 0.5 }}
                            />
                          )} */}
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'secondary.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <Group />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Читатели до 14г.</Typography>
                          <Typography variant="h5">{latestCard.membersUnder14 || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'success.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <Visibility />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Посещения</Typography>
                          <Typography variant="h5">{latestCard.visitsChitalnya || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'warning.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <LocalLibrary />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Посещения със заемане</Typography>
                          <Typography variant="h5">{latestCard.visitsForHome || 0}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Фондове и заеми */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MenuBook fontSize="small" /> Фондове и заеми
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'info.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <Book />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Общ фонд</Typography>
                          <Typography variant="h5">{latestCard.libraryUnits || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'error.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <TrendingUp />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Нови книги</Typography>
                          <Typography variant="h5">{latestCard.newNonPeriodicLibraryUnits || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <Description />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Заети книги</Typography>
                          <Typography variant="h5">{latestCard.borrowedBooks || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar sx={{ bgcolor: 'secondary.light', width: 48, height: 48, mb: 1, mx: 'auto' }}>
                            <EventNote />
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">Заети документи</Typography>
                          <Typography variant="h5">{latestCard.borrowedDocuments || 0}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Активности и събития */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Event fontSize="small" /> Активности и събития
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Културни събития</Typography>
                          <Typography variant="h5">{latestCard.culturalEventsCount || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Посещения събития</Typography>
                          <Typography variant="h5">{latestCard.visitsOrganizedEvents || 0}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                          {latestCard.participationsInRegionalProjects > 0 && (
                            <Chip 
                              icon={<Public />} 
                              label={`${latestCard.participationsInRegionalProjects} регионални проекта`} 
                              size="small" 
                            />
                          )}
                          {latestCard.participationsInNationalProjects > 0 && (
                            <Chip 
                              icon={<EmojiEvents />} 
                              label={`${latestCard.participationsInNationalProjects} национални проекта`} 
                              size="small" 
                              color="primary"
                            />
                          )}
                          {latestCard.participationsInInternationalProjects > 0 && (
                            <Chip 
                              icon={<Language />} 
                              label={`${latestCard.participationsInInternationalProjects} международни проекта`} 
                              size="small" 
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Технологии и персонал */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Computer fontSize="small" /> Технологии и персонал
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Computer fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Компютри за ползватели:</strong> {latestCard.computerAccessForUsers || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Computer fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Компютри за библиотекари:</strong> {latestCard.computerAccessForLibrarians || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Print fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Принтери:</strong> {latestCard.printersCount || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Accessible fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Устройства за хора с увреждания:</strong> {latestCard.devicesForPeopleWithDisabilitiesCount || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Work fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Служители:</strong> {latestCard.employeesCount || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Служители с висше:</strong> {latestCard.employeesWithHigherEducation || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Language fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>Интернет достъп:</strong> {latestCard.internetAccess === 'ДА' ? 'Да' : 'Не'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Исторически данни по години */}
                {sortedCards.length > 1 && (
                  <Grid item xs={12}>
                    <Accordion sx={{ mt: 2 }} defaultExpanded={true}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CompareArrows fontSize="small" /> Исторически данни по години
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Година</TableCell>
                                <TableCell align="right">Читатели</TableCell>
                                <TableCell align="right">Фондове</TableCell>
                                <TableCell align="right">Заеми</TableCell>
                                <TableCell align="right">Посещения</TableCell>
                                <TableCell align="right">Събития</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedCards.map((card) => (
                                <TableRow key={card.year}>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {card.year}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={card.totalMembersCount || 0} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={card.libraryUnits || 0} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={card.borrowedBooks || 0} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={card.visitsChitalnya || 0} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={card.culturalEventsCount || 0} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </>
            )}

            {!latestCard && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="body1" color="text.secondary">
                    Няма налични статистически данни за тази библиотека
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

const LibrariesSection = ({ chitalishteId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const libraries = useSelector(selectLibraries);
  const loading = useSelector(selectLibraryLoading);
  const libraryInformationCards = useSelector((state) => {
    const allCards = {};
    libraries.forEach(lib => {
      const cards = selectLibraryInformationCards(state, lib.id);
      if (cards && cards.length > 0) {
        allCards[lib.id] = cards;
      }
    });
    return allCards;
  });

  useEffect(() => {
    if (chitalishteId) {
      dispatch(fetchLibrariesByChitalishteId(chitalishteId));
    }
  }, [chitalishteId, dispatch]);

  useEffect(() => {
    // Зареждаме информационните карти за всяка библиотека
    libraries.forEach(library => {
      if (!libraryInformationCards[library.id]) {
        dispatch(fetchLibraryInformationCards(library.id));
      }
    });
  }, [libraries, dispatch]);

  if (loading && libraries.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ width: '60%', mx: 'auto', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Зареждане на библиотеки...
        </Typography>
      </Box>
    );
  }

  if (libraries.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
        <LibraryBooks sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Няма налични библиотеки
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Това читалище все още няма регистрирани библиотеки.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Заглавие */}
      {/* <Paper
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LibraryBooks sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Библиотеки при читалището
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {libraries.length} {libraries.length === 1 ? 'библиотека' : 'библиотеки'}
            </Typography>
          </Box>
        </Box>
      </Paper> */}

      {/* Списък с библиотеки */}
      {libraries.map((library, index) => (
        <LibraryCard
          key={library.id}
          library={library}
          informationCards={libraryInformationCards[library.id] || []}
          isFirst={index === 0}
        />
      ))}
    </Box>
  );
};

export default LibrariesSection;